import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Plus, Radar, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { ConfirmDelete, PageHeader, Panel } from "@/admin/ui";
import { useRefresh, useSources } from "@/admin/hooks";

const Dot = ({ status }) => <span className={`h-2 w-2 rounded-full shrink-0 ${!status ? "bg-slate-300" : status === "ok" ? "bg-emerald-500" : "bg-rose-500"}`} />;

const AddSource = ({ onAdded }) => {
  const [f, setF] = useState({ name: "", url: "" });
  const add = useMutation({
    mutationFn: () => api.post("/admin/sources", { ...f, enabled: true }),
    onSuccess: () => { toast.success("Fuente añadida"); setF({ name: "", url: "" }); onAdded(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <Panel title="Añadir fuente" description="Pega la dirección RSS de cualquier medio (suele terminar en .xml o /rss)." className="mb-6" testid="sources-add-panel">
      <form className="grid md:grid-cols-[1fr_2fr_auto] gap-2" onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nombre del medio" data-testid="source-name-input" />
        <Input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://…/rss.xml" data-testid="source-url-input" />
        <Button type="submit" disabled={add.isPending} data-testid="source-add-btn">{add.isPending ? <Loader2 className="animate-spin" /> : <Plus />} Añadir</Button>
      </form>
    </Panel>
  );
};

export default function Sources() {
  const { data } = useSources();
  const refresh = useRefresh();
  const [test, setTest] = useState(null);
  const toggle = useMutation({ mutationFn: (s) => api.put(`/admin/sources/${s.id}`, { ...s, enabled: !s.enabled }), onSuccess: refresh, onError: (e) => toast.error(errMsg(e)) });
  const remove = useMutation({ mutationFn: (s) => api.delete(`/admin/sources/${s.id}`), onSuccess: () => { toast.success("Fuente eliminada"); refresh(); } });
  const probe = useMutation({ mutationFn: (s) => api.post(`/admin/sources/${s.id}/test`).then((r) => ({ ...r.data, name: s.name })), onSuccess: (r) => { setTest(r); refresh(); }, onError: (e) => toast.error(errMsg(e)) });

  return (
    <div data-testid="admin-sources-page">
      <PageHeader kicker="Replicar noticias" title="Fuentes" description="Medios de los que la IA toma los titulares para reescribirlos. Siempre se cita la fuente original." />
      <AddSource onAdded={refresh} />
      <div className="bg-card border border-border" data-testid="sources-list">
        {data?.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center gap-4 px-5 py-4 border-b border-border last:border-0" data-testid={`source-row-${s.id}`}>
            <Dot status={s.last_status === "ok" ? "ok" : s.last_status ? "error" : null} />
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{s.name}</p>
              <p className="text-xs text-muted-foreground truncate">{s.url}</p>
            </div>
            <p className="text-xs text-muted-foreground w-40">{s.last_fetched_at ? `${s.last_count} noticias · ${timeAgo(s.last_fetched_at)}` : "Sin comprobar"}</p>
            <Button size="sm" variant="outline" onClick={() => probe.mutate(s)} disabled={probe.isPending} data-testid={`source-test-${s.id}`}><Radar /> Probar</Button>
            <Switch checked={s.enabled} onCheckedChange={() => toggle.mutate(s)} data-testid={`source-toggle-${s.id}`} />
            <ConfirmDelete title={`¿Eliminar ${s.name}?`} description="Dejará de usarse en las automatizaciones." onConfirm={() => remove.mutate(s)}>
              <Button size="icon" variant="ghost" className="hover:text-destructive" aria-label="Eliminar" data-testid={`source-delete-${s.id}`}><Trash2 /></Button>
            </ConfirmDelete>
          </div>
        ))}
      </div>
      <Dialog open={!!test} onOpenChange={(o) => !o && setTest(null)}>
        <DialogContent data-testid="source-test-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{test?.name}</DialogTitle>
            <DialogDescription>{test?.ok ? `Funciona: ${test.count} noticias disponibles. Estas son las últimas:` : `No se pudo leer: ${test?.status}`}</DialogDescription>
          </DialogHeader>
          <ul className="space-y-2">{test?.items.map((i) => <li key={i.link} className="text-sm border-l-2 border-primary pl-3">{i.title}</li>)}</ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}
